import { Button, Menu } from "@/deps/ui-library";
import { DateRangeField as DateRange } from "@/components/DateField";
import { formatDate } from "@/lib/dates";
import type { DateShortcut } from "./glPeriods";

/**
 * One field for a range: the RDS range picker (Bikram Sambat for a Nepal-pack company), with the
 * shortcuts on the arrow beside it. A shortcut only fills the dates, and the field never shows its
 * name, so what the field says is always what the report will read.
 */
export function DateRangeField({
  from,
  to,
  shortcuts,
  onChange,
}: {
  from: string;
  to: string;
  shortcuts: DateShortcut[];
  onChange: (range: { from?: string; to?: string }) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <DateRange
        aria-label="Period"
        clearable={false}
        value={{ from, to }}
        onChange={(range) => {
          // One bound changes at a time. An emptied bound (cleared, or dropped because the start
          // moved past it) keeps what the report already reads: it never asks for half a period.
          if (range.from && range.from !== from) onChange({ from: range.from });
          else if (range.to && range.to !== to) onChange({ to: range.to });
        }}
      />
      <Menu.Root positioning={{ placement: "bottom-end", gutter: 6 }}>
        <Menu.Trigger asChild>
          <Button.IconButton
            icon="ChevronDown"
            variant="ghost"
            colorScheme="default"
            size="xs"
            aria-label="Date shortcuts"
          />
        </Menu.Trigger>
        <Menu.Content>
          {shortcuts.map((shortcut) => (
            <Menu.Item
              key={shortcut.id}
              value={shortcut.id}
              onClick={() => onChange({ from: shortcut.from, to: shortcut.to })}
            >
              <span className="flex w-full items-center justify-between gap-4">
                <span className={shortcut.from === from && shortcut.to === to ? "font-medium" : ""}>
                  {shortcut.label}
                </span>
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {formatDate(shortcut.from)} – {formatDate(shortcut.to)}
                </span>
              </span>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Root>
    </div>
  );
}
